import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import {
  Botao,
  Chip,
  DataList,
  EmptyState,
  ErrorState,
  Nota,
  PageHeader,
  Skeleton,
} from '../../../ui';

import ModalNovoTipoTermo from '../../../components/modal/ti/ModalNovoTipoTermo';
import EnvioWhatsAppCard from '../components/EnvioWhatsAppCard';
import RegrasDeContrato from '../components/RegrasDeContrato';

import { TI, TITERMO } from '../routes';
import {
  TermoTemplate,
  criarTipoDeTermo,
  listarTemplates,
} from '../../../api/termo-template/termoTemplateApi';
import { formatarDataCurta } from '../../../utils/data';
import { rotuloSituacaoTemplate } from '../../../utils/termoTemplateRegras';
import { extrairMensagemErro } from '../../../utils/apiError';

/**
 * Administração de Termos (at-40, item 43; reescrito na at-69, frente D/D7).
 *
 * ⚠️ A fonte desta tela mudou na at-40: era o espelho `sga_termo`, sincronizado do
 * SGA. Com termos 100% SAAC, o catálogo de tipos é o `termo_template` — carregado
 * a partir do espelho na migration da carga inicial e, dali em diante, nosso.
 *
 * ## O que mudou na at-69
 *
 *   - **Erro deixou de ser mudo**: era `toast` + "Nenhum tipo de termo cadastrado"
 *     — e o TI concluía que o catálogo estava vazio.
 *   - **A explicação dos três estados virou TEXTO** (era `title` no chip) e ganhou
 *     contadores.
 *   - **O botão "Template" virou "Editar template"** (A8): "Template" sozinho não
 *     diz o que o botão faz.
 *   - **O editor virou ficha em sub-rota** (`/ti/termos/:codigo`) — era um modal de
 *     692 linhas com 45 `style` inline e 850px forçados.
 *   - **As "Regras do sistema" ficaram recolhidas** num `Recolhivel`, que é como o
 *     card já se comportava. ⚠️ Os dois cards seguem **empilhados acima** do
 *     catálogo, como hoje (A3 — a Fase 2 não criou abas nesta tela).
 */

type Estado = 'carregando' | 'ok' | 'erro';

const CatalogoTermosPage: React.FC = () => {
  const navigate = useNavigate();
  const voltar = useVoltar(TI());

  const [tipos, setTipos] = useState<TermoTemplate[]>([]);
  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState('');

  const [criandoTipo, setCriandoTipo] = useState(false);
  const [salvandoTipo, setSalvandoTipo] = useState(false);

  const carregar = useCallback(async () => {
    setEstado('carregando');
    try {
      setTipos(await listarTemplates());
      setEstado('ok');
    } catch (falha) {
      setTipos([]);
      setErro(extrairMensagemErro(falha, 'Não foi possível carregar os tipos de termo.'));
      setEstado('erro');
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const confirmarNovoTipo = async (nome: string) => {
    setSalvandoTipo(true);
    try {
      const criado = await criarTipoDeTermo(nome);
      toast.success(
        `Tipo criado com o código ${criado.codigo}. Escreva as cláusulas em "Editar template".`,
      );
      setCriandoTipo(false);
      await carregar();
    } catch (falha) {
      toast.error(extrairMensagemErro(falha, 'Não foi possível cadastrar o tipo de termo.'));
    } finally {
      setSalvandoTipo(false);
    }
  };

  const pendentes = tipos.filter((t) => t.transcricaoPendente).length;
  const geraveis = tipos.filter((t) => t.geravel).length;
  const desativados = tipos.filter((t) => !t.transcricaoPendente && !t.ativo).length;

  const colunas = [
    {
      id: 'codigo',
      rotulo: 'Código',
      secundaria: true,
      celula: (t: TermoTemplate) => `Código ${t.codigo}`,
    },
    {
      id: 'nome',
      rotulo: 'Nome',
      principal: true,
      celula: (t: TermoTemplate) => t.nome || 'Sem nome',
    },
    {
      id: 'titulo',
      rotulo: 'Título no PDF',
      secundaria: true,
      celula: (t: TermoTemplate) => t.titulo || 'Sem título impresso',
    },
    {
      id: 'situacao',
      rotulo: 'Situação',
      celula: (t: TermoTemplate) => {
        const situacao = rotuloSituacaoTemplate(t);
        return <Chip variante={situacao.variante}>{situacao.texto}</Chip>;
      },
    },
    {
      id: 'edicao',
      rotulo: 'Última edição',
      ocultarNoCard: true,
      celula: (t: TermoTemplate) => formatarDataCurta(t.atualizadoEm),
    },
  ];

  const catalogo = () => {
    if (estado === 'carregando') return <Skeleton linhas={5} altura="tw-h-14" />;

    if (estado === 'erro') {
      return (
        <ErrorState
          mensagem="Não foi possível carregar os tipos de termo."
          detalhe={erro}
          aoTentarNovamente={() => void carregar()}
        />
      );
    }

    if (tipos.length === 0) {
      return (
        <EmptyState
          icone="sem-dados"
          titulo="Nenhum tipo de termo cadastrado"
          descricao="Cadastre o primeiro tipo e escreva as cláusulas para que o SAAC possa gerar o contrato."
          acao={<Botao onClick={() => setCriandoTipo(true)}>Novo tipo de termo</Botao>}
        />
      );
    }

    return (
      <DataList
        itens={tipos}
        colunas={colunas}
        chave={(t) => t.codigo}
        rotuloAcessivel="Tipos de termo do catálogo"
        acoes={(t) => (
          <Botao
            variante="secundario"
            tamanho="sm"
            onClick={() => navigate(TITERMO(t.codigo))}
            className="tw-w-full md:tw-w-auto"
          >
            Editar template
          </Botao>
        )}
      />
    );
  };

  return (
    <ShellSaac
      cabecalho={
        <PageHeader
          titulo="Administração de Termos"
          sobretitulo="Administração (TI)"
          aoVoltar={voltar}
          acaoDireita={
            <Botao tamanho="md" iconeEsquerda="mais" onClick={() => setCriandoTipo(true)}>
              Novo tipo de termo
            </Botao>
          }
        />
      }
    >
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-6xl md:tw-p-6">
          <div className="tw-flex tw-flex-col tw-gap-4">
            {/* A3: os dois cards seguem EMPILHADOS acima do catálogo, como hoje. */}
            <EnvioWhatsAppCard />
            <RegrasDeContrato />

            <div className="tw-bg-white tw-p-4 md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-p-6 md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
              <div className="tw-flex tw-flex-col tw-gap-4">
                <h2 className="tw-m-0 tw-font-marca tw-text-base tw-font-bold tw-text-texto-titulo">
                  Tipos de termo
                </h2>

                {/* A carga inicial trouxe os tipos que a associação usa, mas a API da
                    Hinova nunca expôs o TEXTO dos termos — por isso a faixa é sobre
                    transcrição, e não sobre sincronização. */}
                {estado === 'ok' && pendentes > 0 && (
                  <Nota variante="atencao">
                    <strong>
                      {pendentes} {pendentes === 1 ? 'tipo ainda está' : 'tipos ainda estão'} sem
                      texto.
                    </strong>{' '}
                    Eles vieram da carga inicial do catálogo e só podem gerar contrato depois que as
                    cláusulas forem escritas em <strong>Editar template</strong>. Hoje o SAAC gera{' '}
                    {geraveis} de {tipos.length}.
                  </Nota>
                )}

                {estado === 'ok' && tipos.length > 0 && (
                  <div>
                    <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                      <Chip variante="ok">{geraveis} geráveis</Chip>
                      <Chip variante="pendente">{pendentes} sem texto</Chip>
                      <Chip variante="neutro">{desativados} desativados</Chip>
                    </div>
                    {/* 🔴 A explicação dos três estados era um `title` no chip de
                        cada linha — invisível no celular e no teclado (at-65). */}
                    <p className="tw-m-0 tw-mt-1.5 tw-text-xs tw-leading-relaxed tw-text-texto-suave">
                      <strong>Gerável</strong>: o SAAC gera o contrato deste tipo.{' '}
                      <strong>Sem texto</strong>: aguardando a transcrição das cláusulas.{' '}
                      <strong>Desativado</strong>: tem texto, mas está desativado para geração.
                    </p>
                  </div>
                )}

                <Nota>
                  Clique em <strong>Editar template</strong> para editar o texto das cláusulas —
                  cada salvamento publica uma versão, e os contratos já emitidos continuam apontando
                  para a versão com que foram gerados.
                </Nota>

                {catalogo()}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ModalNovoTipoTermo
        aberto={criandoTipo}
        processando={salvandoTipo}
        onConfirmar={(nome) => void confirmarNovoTipo(nome)}
        onCancelar={() => setCriandoTipo(false)}
      />
    </ShellSaac>
  );
};

export default CatalogoTermosPage;
